/**
 * ContactProfile360Modal — Perfil contacto ampliado (0287 Etapa B–F).
 * Presentación UX: Drawer derecho (expande desde el costado; no modal centrado).
 * Contratos: GET /contacts/:id/360-summary | …timeline | …messages | PUT /contacts/:id (identidad)
 * | PUT …/profile-360 | POST …/picture
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import AddIcon from "@material-ui/icons/Add";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import InputAdornment from "@material-ui/core/InputAdornment";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Divider from "@material-ui/core/Divider";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import PersonIcon from "@material-ui/icons/Person";
import ChatIcon from "@material-ui/icons/Chat";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import StopIcon from "@material-ui/icons/Stop";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import NotificationsActiveOutlinedIcon from "@material-ui/icons/NotificationsActiveOutlined";
import AlternateEmailOutlinedIcon from "@material-ui/icons/AlternateEmailOutlined";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";

import api, { getProfilePicUrl } from "../../services/api";
import { i18n } from "../../translate/i18n";
import ImageCropModal from "../ImageCropModal";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";
import AICaptureBadge from "../BusinessProfileCard/AICaptureBadge";

const D11 = {
  green: "#00C853",
  blue: "#0056b3",
  orange: "#FF9800",
  placeholder: "#9E9E9E"
};

/** El Drawer usa Modal con este z-index; el menú por defecto de `Select` queda más bajo (~1300) y no se ve. */
const CONTACT360_DRAWER_Z = 1400;
const CONTACT360_SELECT_MENU_Z = 2000;

/** Evita que objetos `{}` lleguen como hijos a React / TextField. */
function stringifyProfileEditableValue(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") return String(raw);
  if (typeof raw === "object") return ""; // formato inesperado en JSON perfil → no pintar objeto
  return String(raw);
}

/** Cabecera: solo texto plano. */
function textOrEmpty(val) {
  if (typeof val === "string" || typeof val === "number") return String(val);
  return "";
}

/** Claves texto/código persistidas en `profileData` (0287‑H); no incluir `conexionOrigen` ni notas texto plano legacy. */
const PROFILE_PATCH_KEYS = [
  "fechaNacimiento",
  "comoNosConocio",
  "servicioFavorito",
  "profesional",
  "horarioHabitual",
  "tipoCabello",
  "alergias",
  "metodoPago",
  "frecuenciaVisita",
  "asesorAsignado"
];

const REFERRAL_SOURCE_OPTIONS = [
  { value: "facebook", label: "Facebook" },
  { value: "youtube", label: "YouTube" },
  { value: "web", label: "Página web" },
  { value: "recomendacion", label: "Recomendación familiar o amigo" },
  { value: "google", label: "Google / búsqueda" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
  { value: "whatsapp", label: "WhatsApp / otro chat" },
  { value: "otros", label: "Otros" }
];

const HORARIO_HABITUAL_OPTIONS = [
  { value: "manana", label: "Mañana" },
  { value: "tarde", label: "Tarde" },
  { value: "noche", label: "Noche" },
  { value: "fin_semana", label: "Fin de semana" },
  { value: "flexible", label: "Flexible / coordinar" },
  { value: "entre_semana", label: "Entre semana" }
];

const FRECUENCIA_VISITA_OPTIONS = [
  { value: "7", label: "Cada ~7 días (semanal)" },
  { value: "14", label: "Cada ~14 días (quincenal)" },
  { value: "30", label: "Cada ~30 días (mensual)" },
  { value: "60", label: "Cada ~60 días (bimestral)" },
  { value: "90", label: "Cada ~90 días (trimestral)" },
  { value: "esporadico", label: "Esporádico / primera vez" },
  { value: "no_indica", label: "Sin indicar" }
];

const DEFAULT_METODO_PAGO_OPTIONS = [
  { value: "efectivo", label: "Efectivo" },
  { value: "tarjeta", label: "Tarjeta" },
  { value: "transferencia", label: "Transferencia" },
  { value: "yape", label: "Yape" },
  { value: "plin", label: "Plin" },
  { value: "otro", label: "Otro" }
];

function isBeautyBusinessProfile(config) {
  if (!config || typeof config !== "object") return false;
  const b = config.business || config.profile || {};
  const id = b.profileId ?? b.profile_id ?? b.id ?? config.businessProfileId;
  const label = String(b.profileLabel || b.profileName || b.name || b.label || config.profileName || "").toLowerCase();
  const sId = id != null ? String(id).toLowerCase() : "";
  if (sId === "1" || sId === "p1" || sId === "belleza" || sId === "beauty") return true;
  if (label.includes("belleza") || label.includes("beauty") || label.includes("peluquer") || label.includes("salón") || label.includes("salon"))
    return true;
  return false;
}

function buildPaymentMethodOptions(config) {
  const raw = config?.paymentMethods ?? config?.metodosPago ?? config?.payment_methods;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((x) =>
      typeof x === "string"
        ? { value: x.toLowerCase().replace(/\s+/g, "_"), label: x }
        : {
            value: String(x.value ?? x.id ?? x.code ?? x.name ?? "").trim() || String(x.name),
            label: String(x.name ?? x.label ?? x.value ?? "—")
          }
    );
  }
  return DEFAULT_METODO_PAGO_OPTIONS;
}

function toDateInputValue(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = moment(s, ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
}

function normalizeNotasInternasLogFromProfile(pd) {
  const log = pd?.notasInternasLog;
  if (Array.isArray(log)) {
    return log
      .filter((x) => x && typeof x === "object" && String(x.text || "").trim())
      .map((x) => ({
        text: String(x.text).trim(),
        createdAt: typeof x.createdAt === "string" ? x.createdAt : undefined,
        userId: typeof x.userId === "number" ? x.userId : undefined
      }));
  }
  const legacy = pd?.notasInternas;
  if (legacy != null && String(legacy).trim() !== "") {
    return [{ text: String(legacy).trim(), createdAt: undefined }];
  }
  return [];
}

function getAiConfidence(aiExtractedData, key) {
  const v = aiExtractedData?.[key];
  if (v == null) return null;
  if (typeof v === "number") return v;
  if (typeof v === "object" && v.confidence != null) return Number(v.confidence);
  return null;
}

function ProfileField({
  label,
  fieldKey,
  value,
  onChange,
  aiExtractedData,
  multiline,
  rows,
  disabled
}) {
  const confidence = getAiConfidence(aiExtractedData, fieldKey);
  const isAiHint = confidence != null && confidence >= 0.6;
  const display = stringifyProfileEditableValue(value);
  const empty = display === "";

  const inputSx = {};
  if (empty) {
    Object.assign(inputSx, { fontStyle: "italic", color: D11.placeholder });
  }

  let rootSx = {};
  if (isAiHint) {
    rootSx.borderRadius = 8;
    rootSx.borderLeft = `3px solid ${D11.green}`;
  }

  const mergedRootStyle =
    empty || Object.keys(rootSx).length
      ? { ...(empty ? inputSx : {}), ...rootSx }
      : undefined;

  return (
    <Box display="flex" alignItems="flex-start" mb={2} flexWrap="wrap">
      <Typography variant="body2" style={{ minWidth: 160, paddingTop: 8, marginRight: 8 }}>
        {label}
      </Typography>
        <Box flex={1} minWidth={220} display="flex" alignItems="flex-start" flexWrap="wrap">
          <TextField
            disabled={disabled}
            fullWidth
            size="small"
            variant="outlined"
            multiline={multiline}
            rows={multiline ? rows || 4 : undefined}
            value={display}
            placeholder="Sin registrar"
            onChange={(e) => onChange(fieldKey, e.target.value)}
            InputProps={{
              style: mergedRootStyle
            }}
          />
          <Box style={{ marginLeft: 8, marginTop: 4 }}>
            <AICaptureBadge confidence={confidence} />
          </Box>
        </Box>
    </Box>
  );
}

function ProfileDateField({ label, fieldKey, value, onChange, aiExtractedData, disabled }) {
  const confidence = getAiConfidence(aiExtractedData, fieldKey);
  const isAiHint = confidence != null && confidence >= 0.6;
  const display = toDateInputValue(value);
  const empty = display === "";

  const inputSx = {};
  if (empty) {
    Object.assign(inputSx, { fontStyle: "italic", color: D11.placeholder });
  }
  let rootSx = {};
  if (isAiHint) {
    rootSx.borderRadius = 8;
    rootSx.borderLeft = `3px solid ${D11.green}`;
  }
  const mergedRootStyle =
    empty || Object.keys(rootSx).length
      ? { ...(empty ? inputSx : {}), ...rootSx }
      : undefined;

  return (
    <Box display="flex" alignItems="flex-start" mb={2} flexWrap="wrap">
      <Typography variant="body2" style={{ minWidth: 160, paddingTop: 8, marginRight: 8 }}>
        {label}
      </Typography>
      <Box flex={1} minWidth={220} display="flex" alignItems="flex-start" flexWrap="wrap">
        <TextField
          disabled={disabled}
          fullWidth
          size="small"
          variant="outlined"
          type="date"
          value={display}
          placeholder="Sin registrar"
          onChange={(e) => onChange(fieldKey, e.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            style: mergedRootStyle
          }}
        />
        <Box style={{ marginLeft: 8, marginTop: 4 }}>
          <AICaptureBadge confidence={confidence} />
        </Box>
      </Box>
    </Box>
  );
}

function ProfileAiSelect({
  label,
  fieldKey,
  value,
  onChange,
  options,
  aiExtractedData,
  disabled,
  allowEmpty,
  emptyLabel = "Seleccionar…",
  extraLegacyOption
}) {
  const confidence = getAiConfidence(aiExtractedData, fieldKey);
  const isAiHint = confidence != null && confidence >= 0.6;
  const normalized = value == null || value === "" ? "" : String(value);

  const safeOptions = Array.isArray(options) ? options : [];

  let rootSx = {};
  if (isAiHint) {
    rootSx.borderRadius = 8;
    rootSx.borderLeft = `3px solid ${D11.green}`;
  }

  const optionMap = safeOptions.reduce((acc, o) => {
    acc[String(o.value)] = o.label;
    return acc;
  }, {});

  const renderSelected = () => {
    if (normalized === "") {
      return <span style={{ color: D11.placeholder, fontStyle: "italic" }}>{emptyLabel}</span>;
    }
    if (optionMap[normalized]) return optionMap[normalized];
    if (extraLegacyOption) return `Texto guardado: ${extraLegacyOption}`;
    return normalized;
  };

  return (
    <Box display="flex" alignItems="flex-start" mb={2} flexWrap="wrap">
      <Typography variant="body2" style={{ minWidth: 160, paddingTop: 8, marginRight: 8 }}>
        {label}
      </Typography>
      <Box flex={1} minWidth={220} display="flex" alignItems="flex-start" flexWrap="wrap">
        <FormControl
          fullWidth
          size="small"
          variant="outlined"
          disabled={disabled}
          style={{ ...rootSx }}
        >
          <Select
            displayEmpty
            value={normalized}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            renderValue={() => renderSelected()}
            MenuProps={{
              disableScrollLock: true,
              getContentAnchorEl: null,
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              /* No usar ModalProps aquí: Select v4 lo filtra mal y React lo pinta sobre un DOM <div>. */
              style: { zIndex: CONTACT360_SELECT_MENU_Z },
              PaperProps: {
                style: { maxHeight: 360, zIndex: CONTACT360_SELECT_MENU_Z }
              }
            }}
          >
            {allowEmpty !== false ? (
              <MenuItem value="">
                <em>{emptyLabel}</em>
              </MenuItem>
            ) : null}
            {extraLegacyOption && normalized && !safeOptions.some((o) => String(o.value) === normalized) ? (
              <MenuItem value={normalized}>Texto guardado: {extraLegacyOption}</MenuItem>
            ) : null}
            {safeOptions.map((o) => (
              <MenuItem key={String(o.value)} value={String(o.value)}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box style={{ marginLeft: 8, marginTop: 4 }}>
          <AICaptureBadge confidence={confidence} />
        </Box>
      </Box>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: "min(920px, max(380px, 36vw))",
    maxWidth: "100%",
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column"
  },
  drawerHeader: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    padding: "14px 16px 12px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)"
  },
  drawerScroll: {
    flex: 1,
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "12px 20px 24px",
    "&:focus": { outline: "none" }
  },
  kpiPaper: {
    padding: "10px 12px",
    textAlign: "center",
    borderRadius: 8,
    minHeight: 64,
    flex: "1 1 120px"
  },
  kpiValue: { fontWeight: 700, fontSize: 18, color: D11.blue },
  kpiOrange: { color: D11.orange },
  kpiGreen: { color: D11.green },
  bannerIa: {
    backgroundColor: "rgba(0, 200, 83, 0.09)",
    border: `1px solid ${D11.green}`,
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 16
  },
  sectionTitle: {
    fontWeight: 700,
    marginTop: 16,
    marginBottom: 8,
    letterSpacing: 0.5,
    color: D11.blue
  },
  saveBtn: {
    backgroundColor: D11.green,
    color: "#fff",
    "&:hover": { backgroundColor: "#00a843" },
    marginTop: 24,
    width: "100%"
  },
  tabIndicator: {
    height: 3,
    backgroundColor: D11.blue
  },
  timelineEvent: {
    padding: "12px 14px",
    marginBottom: 10,
    borderRadius: 8,
    borderLeft: `3px solid ${D11.blue}`,
    backgroundColor: "#fafafa"
  },
  timelineDate: {
    fontSize: 12,
    color: D11.placeholder,
    marginBottom: 4
  },
  headerAvatarWrap: {
    position: "relative",
    display: "inline-block",
    flexShrink: 0
  },
  headerAvatarCam: {
    position: "absolute",
    bottom: 0,
    right: 0,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: 4,
    borderRadius: "50%",
    "&:hover": { backgroundColor: theme.palette.primary.dark }
  },
  hiddenFileInput: { display: "none" },
  avatarHeader: {
    width: 64,
    height: 64,
    minWidth: 64,
    minHeight: 64,
    "& img": { objectFit: "cover", width: "100%", height: "100%" }
  }
}));

const STATUS_LABEL = {
  pending: "Pendiente confirmar",
  confirmed: "Confirmada",
  rescheduled: "Reprogramada",
  in_progress: "En curso",
  completed: "Completada",
  no_show: "No asistió",
  cancelled: "Cancelada",
  canceled: "Cancelada"
};

function statusChipStyle(status) {
  const s = (status || "").toLowerCase();
  if (s === "completed") return { backgroundColor: "rgba(0, 200, 83, 0.15)", color: "#1b5e20", border: `1px solid ${D11.green}` };
  if (s === "cancelled" || s === "canceled") return { backgroundColor: "rgba(211, 47, 47, 0.1)", color: "#c62828", border: "1px solid #ef5350" };
  if (s === "pending") return { backgroundColor: "rgba(255, 152, 0, 0.15)", color: "#e65100", border: `1px solid ${D11.orange}` };
  if (s === "confirmed") return { backgroundColor: "rgba(0, 200, 83, 0.12)", color: "#2e7d32", border: `1px solid ${D11.green}` };
  return { backgroundColor: "#f5f5f5", color: "#424242", border: "1px solid #e0e0e0" };
}

function AppointmentRowCard({ row, highlight }) {
  const label = STATUS_LABEL[row.status] || row.status || "—";
  const fmt = moment(row.appointmentDate).format("DD/MM/YYYY HH:mm");
  const svc =
    row.serviceName != null && typeof row.serviceName !== "object"
      ? String(row.serviceName)
      : "Servicio sin nombre";
  const prof =
    row.professionalName != null && typeof row.professionalName !== "object"
      ? String(row.professionalName)
      : "—";
  return (
    <Paper
      variant="outlined"
      style={{
        padding: 12,
        marginBottom: 10,
        borderRadius: 8,
        ...(highlight ? { backgroundColor: "rgba(0, 86, 179, 0.06)", borderColor: D11.blue } : {})
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          {svc}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          {row.bookedByAi ? (
            <Chip size="small" label="IA" style={{ height: 22, backgroundColor: D11.green, color: "#fff" }} />
          ) : null}
          <Chip size="small" label={label} style={{ height: 24, ...statusChipStyle(row.status) }} />
        </Box>
      </Box>
      <Box display="flex" alignItems="center" mt={1} style={{ gap: 6, color: D11.blue }}>
        <CalendarTodayIcon style={{ fontSize: 18 }} />
        <Typography variant="body2">{fmt}</Typography>
        {row.durationMin ? (
          <Typography variant="caption" color="textSecondary">
            · {row.durationMin} min
          </Typography>
        ) : null}
      </Box>
      <Box display="flex" alignItems="center" mt={0.5} style={{ gap: 6 }}>
        <PersonIcon style={{ fontSize: 18, color: D11.placeholder }} />
        <Typography variant="body2">{prof}</Typography>
      </Box>
    </Paper>
  );
}

function Contact360CitasTab({ appointments, kpis, classes, excludeFromSequences, savingExclude, onExcludeChange }) {
  const proximaId = kpis?.proximaCita?.id;
  const proximaRow = useMemo(
    () => (proximaId ? appointments.find((a) => a.id === proximaId) : null),
    [appointments, proximaId]
  );

  const completedCount = useMemo(
    () => appointments.filter((a) => (a.status || "").toLowerCase() === "completed").length,
    [appointments]
  );

  const cancelledCountUi = useMemo(
    () =>
      appointments.filter((a) => {
        const s = (a.status || "").toLowerCase();
        return s === "cancelled" || s === "canceled";
      }).length,
    [appointments]
  );

  const historialRows = useMemo(() => {
    const nowMs = Date.now();
    return [...appointments]
      .filter((a) => {
        if (proximaId != null && a.id === proximaId) return false;
        const t = new Date(a.appointmentDate).getTime();
        const s = (a.status || "").toLowerCase();
        const terminated = s === "completed" || s === "cancelled" || s === "canceled" || s === "no_show";
        return t < nowMs || terminated;
      })
      .sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate));
  }, [appointments, proximaId]);

  return (
    <Box mt={2}>
      <Box display="flex" flexWrap="wrap" style={{ gap: 10 }} mb={2}>
        <Paper className={classes.kpiPaper} variant="outlined">
          <Typography variant="caption">Total citas</Typography>
          <Typography style={{ fontWeight: 700, fontSize: 18, color: D11.blue }}>{appointments.length}</Typography>
        </Paper>
        <Paper className={classes.kpiPaper} variant="outlined">
          <Typography variant="caption">Completadas</Typography>
          <Typography style={{ fontWeight: 700, fontSize: 18, color: D11.green }}>{completedCount}</Typography>
        </Paper>
        <Paper className={classes.kpiPaper} variant="outlined">
          <Typography variant="caption">Canceladas</Typography>
          <Typography style={{ fontWeight: 700, fontSize: 18, color: "#c62828" }}>{cancelledCountUi}</Typography>
        </Paper>
      </Box>

      <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue, marginBottom: 8 }}>
        PRÓXIMA CITA
      </Typography>
      {proximaRow ? (
        <AppointmentRowCard row={proximaRow} highlight />
      ) : (
        <Typography variant="body2" style={{ fontStyle: "italic", color: D11.placeholder, marginBottom: 16 }}>
          Sin próxima cita programada
        </Typography>
      )}

      <Divider style={{ margin: "16px 0" }} />

      <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue, marginBottom: 8 }}>
        HISTORIAL DE CITAS
      </Typography>
      {historialRows.length === 0 ? (
        <Typography variant="body2" style={{ fontStyle: "italic", color: D11.placeholder }}>
          Sin citas en historial
        </Typography>
      ) : (
        historialRows.map((row) => <AppointmentRowCard key={row.id} row={row} />)
      )}

      <Divider style={{ margin: "20px 0" }} />

      <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.placeholder, marginBottom: 8 }}>
        SECUENCIAS AUTOMÁTICAS
      </Typography>
      <Paper variant="outlined" style={{ padding: 16, borderRadius: 8, backgroundColor: "#fafafa" }}>
        <Typography variant="body2" style={{ marginBottom: 12, color: "#616161" }}>
          Detalle de envíos programados se ampliará con datos de automatización. Puede excluir al contacto de mensajes
          automáticos de secuencia.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={!!excludeFromSequences}
              onChange={(e) => onExcludeChange(e.target.checked)}
              disabled={savingExclude}
            />
          }
          label={
            <Box>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                Excluir de secuencias
              </Typography>
              <Typography variant="caption" style={{ color: D11.placeholder }}>
                No enviar mensajes automáticos a este número (alcance global, B6).
              </Typography>
            </Box>
          }
        />
        {savingExclude ? (
          <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={18} />
            <Typography variant="caption">Guardando…</Typography>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}

const TIMELINE_PAGE = 25;

function emptyHistorialState() {
  return {
    events: [],
    iaHints: [],
    total: 0,
    loading: false,
    loadingMore: false,
    timelineRestrictedMsg: null
  };
}

function Contact360HistorialTab({
  classes,
  loading,
  loadingMore,
  events,
  iaHints,
  total,
  timelineRestrictedMsg,
  onLoadMore
}) {
  const hasMore = events.length < total;

  return (
    <Box mt={2}>
      <Paper className={classes.bannerIa} elevation={0} style={{ marginBottom: 12 }}>
        <Typography variant="body2">
          Línea de tiempo operativa del contacto: alta en CRM, conversaciones registradas como tickets y citas (según
          permisos de agenda). Sugerencias IA del perfil se listan aparte cuando existen datos.
        </Typography>
      </Paper>

      {timelineRestrictedMsg && (
        <Paper variant="outlined" style={{ padding: 12, marginBottom: 12, borderColor: D11.orange }}>
          <Typography variant="body2">{timelineRestrictedMsg}</Typography>
          <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginTop: 6 }}>
            Los eventos de citas no se incluyen en esta vista para tu usuario; verás alta de contacto y tickets si
            aplica.
          </Typography>
        </Paper>
      )}

      {Array.isArray(iaHints) && iaHints.length > 0 ? (
        <Box mb={2}>
          <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.green, marginBottom: 8 }}>
            SUGERENCIAS IA (PERFIL)
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
            {iaHints.map((h, idx) => (
              <Chip
                key={`${h.fieldKey}-${idx}`}
                size="small"
                label={(() => {
                  if (typeof h.confidence !== "number" || Number.isNaN(h.confidence)) return h.label;
                  const pct = h.confidence <= 1 && h.confidence >= 0 ? Math.round(h.confidence * 100) : Math.round(h.confidence);
                  return `${h.label} · IA ${pct}%`;
                })()}
                style={{
                  borderLeft: `3px solid ${D11.green}`,
                  backgroundColor: "rgba(0, 200, 83, 0.08)"
                }}
              />
            ))}
          </Box>
        </Box>
      ) : null}

      <Divider style={{ margin: "16px 0" }} />

      <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue, marginBottom: 8 }}>
        ACTIVIDAD
      </Typography>

      {loading && events.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={36} />
        </Box>
      ) : events.length === 0 ? (
        <Typography variant="body2" style={{ fontStyle: "italic", color: D11.placeholder }}>
          Sin eventos registrados para este contacto.
        </Typography>
      ) : (
        events.map((ev, idx) => {
          const Icon =
            ev.kind === "appointment" ? CalendarTodayIcon : ev.kind === "ticket" ? ChatIcon : PersonIcon;
          const borderLeft =
            ev.kind === "appointment" ? `3px solid ${D11.green}` : ev.kind === "ticket" ? "3px solid #5c6bc0" : `3px solid ${D11.blue}`;
          const when = moment(ev.occurredAt).format("DD/MM/YYYY HH:mm");
          const detailSafe = typeof ev.detail === "string" ? ev.detail : "";

          return (
            <Paper
              key={`${ev.kind}-${ev.refId ?? "na"}-${ev.occurredAt}-${idx}`}
              variant="outlined"
              elevation={0}
              className={classes.timelineEvent}
              style={{ borderLeft }}
            >
              <Box display="flex" alignItems="flex-start" style={{ gap: 10 }}>
                <Icon style={{ fontSize: 22, marginTop: 2, color: D11.placeholder }} />
                <Box flex={1} minWidth={0}>
                  <Typography className={classes.timelineDate}>{when}</Typography>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, color: D11.blue }}>
                    {textOrEmpty(ev.title) || ev.kind || "—"}
                  </Typography>
                  {detailSafe ? (
                    <Typography variant="body2" style={{ marginTop: 4, color: "#424242", wordBreak: "break-word" }}>
                      {detailSafe}
                    </Typography>
                  ) : null}
                  {typeof ev.refId === "number" ? (
                    <Typography variant="caption" style={{ color: D11.placeholder }}>
                      Ref. #{ev.refId}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Paper>
          );
        })
      )}

      {hasMore ? (
        <Box mt={2} display="flex" justifyContent="center">
          <Button variant="outlined" color="primary" size="small" disabled={loadingMore} onClick={onLoadMore}>
            {loadingMore ? <CircularProgress size={22} /> : "Cargar más"}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

function campaignStatusChipStyle(status) {
  const s = (status || "").toUpperCase();
  if (s === "PROGRAMADA") return { borderColor: "#1976d2", color: "#1565c0", backgroundColor: "rgba(25,118,210,0.08)" };
  if (s === "EM_ANDAMENTO") return { borderColor: D11.green, color: "#2e7d32", backgroundColor: "rgba(0,200,83,0.1)" };
  if (s === "CANCELADA") return { borderColor: "#ef5350", color: "#c62828", backgroundColor: "rgba(211,47,47,0.08)" };
  if (s === "FINALIZADA") return { borderColor: D11.green, color: "#1b5e20", backgroundColor: "rgba(0,200,83,0.06)" };
  if (s === "INATIVA") return { borderColor: "#bdbdbd", color: "#757575", backgroundColor: "#f5f5f5" };
  return { borderColor: "#e0e0e0", color: "#424242", backgroundColor: "#fafafa" };
}

function Contact360MensajesTab({ classes, contactId }) {
  const history = useHistory();
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [pauseTarget, setPauseTarget] = useState(null);
  const [stopTarget, setStopTarget] = useState(null);
  const [mutatingId, setMutatingId] = useState(null);

  const fetchData = useCallback(
    async (forceRefresh) => {
      if (!contactId) return;
      try {
        setLoading(true);
        const { data } = await api.get(`/contacts/${contactId}/360-messages`, {
          params: forceRefresh ? { refresh: true } : {}
        });
        if (!mountedRef.current) return;
        setPayload(data);
      } catch (e) {
        if (!mountedRef.current) return;
        toastError(e);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [contactId]
  );

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const kpis = payload?.kpis || {};
  const secuencias = Array.isArray(payload?.secuencias) ? payload.secuencias : [];
  const campanas = Array.isArray(payload?.campanas) ? payload.campanas : [];
  const canManageCampaigns = !!payload?.capabilities?.canManageCampaigns;
  const fromCache = !!payload?.meta?.fromCache;

  const refreshAfterMutation = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  const confirmPause = async () => {
    if (!pauseTarget?.id) return;
    try {
      setMutatingId(pauseTarget.id);
      await api.post(`/campaigns/${pauseTarget.id}/cancel`);
      if (!mountedRef.current) return;
      toast.success("Campaña pausada");
      setPauseTarget(null);
      await refreshAfterMutation();
    } catch (e) {
      toastError(e);
    } finally {
      if (mountedRef.current) setMutatingId(null);
    }
  };

  const confirmStop = async () => {
    if (!stopTarget?.id) return;
    try {
      setMutatingId(stopTarget.id);
      await api.post(`/campaigns/${stopTarget.id}/stop`);
      if (!mountedRef.current) return;
      toast.success("Campaña detenida");
      setStopTarget(null);
      await refreshAfterMutation();
    } catch (e) {
      toastError(e);
    } finally {
      if (mountedRef.current) setMutatingId(null);
    }
  };

  const restartCampaign = async (campaign) => {
    if (!campaign?.id) return;
    try {
      setMutatingId(campaign.id);
      await api.post(`/campaigns/${campaign.id}/restart`);
      if (!mountedRef.current) return;
      toast.success("Campaña reanudada");
      await refreshAfterMutation();
    } catch (e) {
      toastError(e);
    } finally {
      if (mountedRef.current) setMutatingId(null);
    }
  };

  return (
    <Box mt={2}>
      <Paper className={classes.bannerIa} elevation={0} style={{ marginBottom: 12 }}>
        <Typography variant="body2">
          Resumen operativo del canal WhatsApp para este número: KPIs de mensajes, tiempo medio de primera respuesta
          humana, envíos programados (secuencia) y participación en campañas masivas. Los datos KPI usan cache de 15 min
          (excepto tras actualizar o tras pausar / detener campañas).
        </Typography>
      </Paper>

      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }} mb={1}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue }}>
          INDICADORES MENSAJERÍA
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          {fromCache ? (
            <Typography variant="caption" style={{ color: D11.placeholder }}>
              Cache TTL 15 min
            </Typography>
          ) : null}
          <Button variant="text" color="primary" size="small" disabled={loading} onClick={() => fetchData(true)}>
            Actualizar KPIs
          </Button>
        </Box>
      </Box>

      {loading && !payload ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={36} />
        </Box>
      ) : (
        <Box display="flex" flexWrap="wrap" style={{ gap: 10 }} mb={2}>
          <Paper className={classes.kpiPaper} variant="outlined">
            <Typography variant="caption">Enviados (humano)</Typography>
            <Typography className={classes.kpiValue}>
              {kpis.mensajesEnviados != null ? kpis.mensajesEnviados : "—"}
            </Typography>
          </Paper>
          <Paper className={classes.kpiPaper} variant="outlined">
            <Typography variant="caption">Automáticos</Typography>
            <Typography className={classes.kpiOrange}>
              {kpis.mensajesAutomaticos != null ? kpis.mensajesAutomaticos : "—"}
            </Typography>
          </Paper>
          <Paper className={classes.kpiPaper} variant="outlined">
            <Typography variant="caption">Conversaciones</Typography>
            <Typography className={classes.kpiGreen}>
              {kpis.conversacionesTotales != null ? kpis.conversacionesTotales : "—"}
            </Typography>
          </Paper>
          <Paper className={classes.kpiPaper} variant="outlined">
            <Typography variant="caption">TMR 1ª respuesta</Typography>
            <Typography className={classes.kpiGreen} variant="subtitle2">
              {kpis.tiempoMedioRespuestaLabel || "Sin datos"}
            </Typography>
          </Paper>
        </Box>
      )}

      <Divider style={{ margin: "14px 0" }} />

      <Box display="flex" alignItems="center" mb={1} style={{ gap: 8 }}>
        <NotificationsActiveOutlinedIcon style={{ fontSize: 22, color: D11.blue }} />
        <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue }}>
          SECUENCIAS / RECORDATORIOS (Schedule)
        </Typography>
      </Box>
      {secuencias.length === 0 ? (
        <Typography variant="body2" style={{ fontStyle: "italic", color: D11.placeholder, marginBottom: 12 }}>
          Sin envíos programados para este contacto.
        </Typography>
      ) : (
        secuencias.map((s) => (
          <Paper key={s.id} variant="outlined" style={{ padding: 12, marginBottom: 10, borderRadius: 8 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" style={{ gap: 8 }}>
              <Typography variant="caption" style={{ color: D11.placeholder }}>
                #{s.id} · Envío previsto{" "}
                {s.sendAt ? moment(s.sendAt).format("DD/MM/YYYY HH:mm") : "—"}
              </Typography>
              <Chip size="small" label={s.badge || "Otro"} style={{ height: 24 }} variant="outlined" />
            </Box>
            {s.sentAt ? (
              <Typography variant="caption" display="block" style={{ color: D11.green }}>
                Entregado {moment(s.sentAt).format("DD/MM/YYYY HH:mm")}
              </Typography>
            ) : null}
            <Typography variant="body2" style={{ marginTop: 8, wordBreak: "break-word", color: "#424242" }}>
              {typeof s.preview === "string" ? s.preview : "—"}
            </Typography>
            <Typography variant="caption" style={{ color: D11.placeholder }}>
              Estado sistema: {s.status || "—"}
            </Typography>
          </Paper>
        ))
      )}

      <Divider style={{ margin: "18px 0" }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <AlternateEmailOutlinedIcon style={{ fontSize: 22, color: D11.blue }} />
          <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue }}>
            CAMPAÑAS (por número WhatsApp)
          </Typography>
        </Box>
        <Button size="small" color="primary" variant="text" onClick={() => history.push("/campaigns")}>
          Ir a Campañas
        </Button>
      </Box>
      {!canManageCampaigns ? (
        <Typography variant="caption" display="block" style={{ marginTop: 4, marginBottom: 12, color: D11.placeholder }}>
          Las acciones Pausar / Parar / Reanudar solo están disponibles para administradores.
        </Typography>
      ) : null}
      {campanas.length === 0 ? (
        <Typography variant="body2" style={{ fontStyle: "italic", color: D11.placeholder, marginTop: 8 }}>
          Sin envíos agrupados en campañas para este número en la empresa.
        </Typography>
      ) : (
        campanas.map((c) => (
          <Paper key={`camp-${c.id}`} variant="outlined" style={{ padding: 12, marginTop: 10, borderRadius: 8 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" style={{ gap: 10 }}>
              <Box flex={1} minWidth={200}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, color: D11.blue }}>
                  {typeof c.name === "string" ? c.name : `Campaña #${c.id}`}
                </Typography>
                <Typography variant="caption" style={{ display: "block", color: D11.placeholder }}>
                  ID {c.id} · Ejec./envíos relacionados ~{typeof c.shippingCount === "number" ? c.shippingCount : 0}{" "}
                  {c.lastActivityAt ? `· Últ. actividad ${moment(c.lastActivityAt).format("DD/MM/YYYY HH:mm")}` : ""}
                </Typography>
              </Box>
              <Chip
                label={typeof c.status === "string" ? c.status.replace(/_/g, " ") : "—"}
                size="small"
                variant="outlined"
                style={{ height: 26, ...campaignStatusChipStyle(c.status) }}
              />
            </Box>
            {canManageCampaigns ? (
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 4, marginTop: 10 }}>
                {String(c.status || "").toUpperCase() === "EM_ANDAMENTO" ? (
                  <IconButton
                    aria-label="Pausar campaña"
                    size="small"
                    title="Pausar campaña"
                    disabled={mutatingId === c.id}
                    onClick={() => setPauseTarget({ id: c.id, name: c.name })}
                  >
                    <PauseCircleOutlineIcon style={{ fontSize: 26, color: "#1976d2" }} />
                  </IconButton>
                ) : null}
                {(String(c.status || "").toUpperCase() === "PROGRAMADA" ||
                  String(c.status || "").toUpperCase() === "EM_ANDAMENTO") && (
                  <IconButton
                    aria-label="Parar campaña definitivamente"
                    size="small"
                    title="Parar campaña definitivamente"
                    disabled={mutatingId === c.id}
                    onClick={() => setStopTarget({ id: c.id, name: c.name })}
                  >
                    <StopIcon style={{ fontSize: 24, color: "#d32f2f" }} />
                  </IconButton>
                )}
                {String(c.status || "").toUpperCase() === "CANCELADA" &&
                typeof c.nextScheduledAt === "string" &&
                c.nextScheduledAt.trim() !== "" ? (
                  <IconButton
                    aria-label="Reanudar campaña"
                    size="small"
                    title="Reanudar campaña"
                    disabled={mutatingId === c.id}
                    onClick={() => restartCampaign(c)}
                  >
                    <PlayCircleOutlineIcon style={{ fontSize: 28, color: D11.green }} />
                  </IconButton>
                ) : null}
              </Box>
            ) : null}
          </Paper>
        ))
      )}

      <Dialog open={!!pauseTarget} onClose={() => !mutatingId && setPauseTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Pausar campaña</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            ¿Pausar esta campaña? Los mensajes ya enviados no se revierten. Podrá reanudarla después si corresponde.
          </Typography>
          {pauseTarget ? (
            <Typography variant="subtitle2" style={{ marginTop: 16 }}>
              {pauseTarget.name}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setPauseTarget(null)} disabled={mutatingId != null}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={confirmPause} disabled={mutatingId != null}>
            {mutatingId ? <CircularProgress size={22} /> : "Pausar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!stopTarget} onClose={() => !mutatingId && setStopTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ color: "#d32f2f" }}>Parar campaña definitivamente</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
            Acción muy restrictiva: no siempre permite reactivar desde aquí según configuración recurrente / estados del
            motor.
          </Typography>
          {stopTarget ? (
            <Typography variant="subtitle2">{stopTarget.name}</Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setStopTarget(null)} disabled={mutatingId != null}>
            Volver
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#d32f2f", color: "#fff" }}
            onClick={confirmStop}
            disabled={mutatingId != null}
          >
            {mutatingId ? <CircularProgress size={22} /> : "Parar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const NEGOCIO_SALE_STATUS = {
  paid: { label: "Pagada", color: "#00C853" },
  pending: { label: "Pendiente", color: "#FF9800" },
  pending_payment: { label: "Esperando pago", color: "#FF9800" },
  cancelled: { label: "Cancelada", color: "#9E9E9E" }
};

const formatNegocioMoney = (negocio, n) =>
  `${negocio?.currencySymbol || "$"}${Number(n || 0).toLocaleString("es-CO", { maximumFractionDigits: 2 })}`;

const formatNegocioWallet = (negocio) =>
  negocio?.walletCurrency
    ? `$${Number(negocio.wallet || 0).toLocaleString("es-CO", { maximumFractionDigits: negocio.walletCurrency === "COP" ? 0 : 2 })} ${negocio.walletCurrency}`
    : formatNegocioMoney(negocio, negocio?.wallet);

function Contact360TiendaTab({ negocio, classes }) {
  const services = negocio.services || [];
  const sales = negocio.sales || [];
  const serviceState = (s) => {
    if (s.status === "expired") return { label: s.daysLeft != null ? `Vencido hace ${Math.abs(s.daysLeft)} d` : "Vencido", color: "#E53935" };
    if (s.daysLeft == null) return { label: "Sin vencimiento", color: D11.blue };
    if (s.daysLeft <= 5) return { label: s.daysLeft === 0 ? "Vence hoy" : `Vence en ${s.daysLeft} d`, color: D11.orange };
    return { label: `${s.daysLeft} d restantes`, color: D11.green };
  };

  return (
    <Box mt={2}>
      <Typography className={classes.sectionTitle}>DATOS DE LA TIENDA</Typography>
      <Paper variant="outlined" style={{ padding: "10px 14px", marginBottom: 14, borderRadius: 8 }}>
        <Box display="flex" flexWrap="wrap" style={{ gap: 16 }}>
          {negocio.customerType ? <Typography variant="body2"><strong>Tipo:</strong> {negocio.customerType}</Typography> : null}
          {negocio.registeredAt ? (
            <Typography variant="body2"><strong>Cliente desde:</strong> {moment(negocio.registeredAt).format("DD/MM/YYYY")}</Typography>
          ) : null}
          {negocio.country ? <Typography variant="body2"><strong>País:</strong> {negocio.country}</Typography> : null}
          {negocio.telegram ? <Typography variant="body2"><strong>Telegram:</strong> {negocio.telegram}</Typography> : null}
          {negocio.lastPurchaseAt ? (
            <Typography variant="body2"><strong>Última compra:</strong> {moment(negocio.lastPurchaseAt).format("DD/MM/YYYY")}</Typography>
          ) : null}
          {negocio.customerType === "reseller" && !negocio.pointsEnabled ? null : (
          <Typography variant="body2">
            <strong>Saldo:</strong> {negocio.pointsEnabled ? formatNegocioWallet(negocio) : "Desactivado"}
          </Typography>
          )}
        </Box>
      </Paper>

      <Typography className={classes.sectionTitle}>SERVICIOS ({services.length})</Typography>
      {services.length === 0 ? (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>Sin servicios registrados.</Typography>
      ) : (
        <Box display="flex" flexDirection="column" style={{ gap: 8, marginBottom: 14 }}>
          {services.map((s) => {
            const st = serviceState(s);
            return (
              <Paper key={s.id} variant="outlined" style={{ padding: "8px 12px", borderRadius: 8, borderLeft: `4px solid ${st.color}` }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>{s.productName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {s.startDate ? moment(s.startDate).format("DD/MM/YYYY") : "—"} → {s.endDate ? moment(s.endDate).format("DD/MM/YYYY") : "—"}
                      {s.price ? ` · ${formatNegocioMoney(negocio, s.price)}` : ""}
                    </Typography>
                  </Box>
                  <Chip size="small" label={st.label} style={{ backgroundColor: st.color, color: "#fff" }} />
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <Typography className={classes.sectionTitle}>ÚLTIMAS COMPRAS</Typography>
      {sales.length === 0 ? (
        <Typography variant="body2" color="textSecondary">Sin compras registradas.</Typography>
      ) : (
        <Box display="flex" flexDirection="column" style={{ gap: 6 }}>
          {sales.map((sale) => {
            const st = NEGOCIO_SALE_STATUS[sale.status] || { label: sale.status, color: D11.blue };
            return (
              <Box key={sale.id} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                <Typography variant="body2">
                  #{sale.id} · {sale.date ? moment(sale.date).format("DD/MM/YYYY") : "—"}
                  {sale.paymentMethod ? ` · ${sale.paymentMethod}` : ""}
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>{formatNegocioMoney(negocio, sale.total)}</Typography>
                  <Chip size="small" label={st.label} style={{ backgroundColor: st.color, color: "#fff" }} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default function ContactProfile360Modal({
  open,
  onClose,
  contactId,
  onContactUpdated,
  /** Incrementar desde el padre (p. ej. socket) para forzar refresco desde API. */
  reloadContactSignal = 0
}) {
  const classes = useStyles();
  const mountedRef = React.useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [onboardingConfig, setOnboardingConfig] = useState(null);
  const [draft, setDraft] = useState({});
  const [tagDraft, setTagDraft] = useState("");
  const [notaInput, setNotaInput] = useState("");
  const [savingExclude, setSavingExclude] = useState(false);
  const [historial, setHistorial] = useState(() => emptyHistorialState());

  /** Columnas `Contacts`: nombre, WhatsApp y correo (PUT /contacts, no sólo perfil JSON). */
  const [identity, setIdentity] = useState({ name: "", number: "", email: "" });
  const [avatarKey, setAvatarKey] = useState(0);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedCropSource, setSelectedCropSource] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const pictureInputRef = useRef(null);

  const loadSummary = useCallback(async () => {
    if (!contactId) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/contacts/${contactId}/360-summary`);
      if (!mountedRef.current) return;
      const pd = data?.contact?.profileData || {};
      const nextDraft = {};
      PROFILE_PATCH_KEYS.forEach((k) => {
        const v = pd[k];
        nextDraft[k] =
          v != null && v !== "" && typeof v !== "object" ? String(v) : "";
      });
      nextDraft.contactTags = Array.isArray(pd.contactTags)
        ? [...pd.contactTags]
            .map((x) => (typeof x === "string" ? x.trim() : ""))
            .filter(Boolean)
        : [];
      nextDraft.excludeFromSequences = !!pd.excludeFromSequences;
      nextDraft.notasInternasLog = normalizeNotasInternasLogFromProfile(pd);
      setDraft(nextDraft);
      setSummary(data);
      const c = data?.contact;
      setIdentity({
        name: c?.name != null ? String(c.name) : "",
        number: c?.number != null ? String(c.number) : "",
        email: c?.email != null ? String(c.email) : ""
      });
      setTagDraft("");
      setNotaInput("");
    } catch (e) {
      toastError(e);
      setSummary(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/onboarding/config");
        if (!mountedRef.current || cancelled) return;
        setOnboardingConfig(data && typeof data === "object" ? data : {});
      } catch {
        if (!mountedRef.current || cancelled) return;
        setOnboardingConfig({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const fetchTimelinePage = useCallback(
    async (reset) => {
      if (!contactId) return;
      let offset = 0;
      setHistorial((prev) => {
        offset = reset ? 0 : prev.events.length;
        return {
          ...prev,
          loading: !!reset,
          loadingMore: !reset
        };
      });
      try {
        const { data } = await api.get(`/contacts/${contactId}/360-timeline`, {
          params: { limit: TIMELINE_PAGE, offset }
        });
        if (!mountedRef.current) return;
        const nextEvents = Array.isArray(data.events) ? data.events : [];
        const nextTotal = typeof data.total === "number" ? data.total : 0;
        setHistorial((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false,
          events: reset ? nextEvents : [...prev.events, ...nextEvents],
          iaHints: reset && Array.isArray(data.iaHints) ? data.iaHints : prev.iaHints,
          total: nextTotal,
          timelineRestrictedMsg:
            data.permissions?.restrictedMessage != null
              ? String(data.permissions.restrictedMessage)
              : null
        }));
      } catch (e) {
        if (!mountedRef.current) return;
        toastError(e);
        setHistorial((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false
        }));
      }
    },
    [contactId]
  );

  useEffect(() => {
    if (!open) return;
    setTabIndex(0);
    setHistorial(emptyHistorialState());
    setOnboardingConfig(null);
    loadSummary();
  }, [open, loadSummary]);

  useEffect(() => {
    if (!open || !contactId || !reloadContactSignal) return;
    loadSummary();
  }, [reloadContactSignal, open, contactId, loadSummary]);

  useEffect(() => {
    if (!open || tabIndex !== 2 || !contactId) return;
    fetchTimelinePage(true);
  }, [open, tabIndex, contactId, fetchTimelinePage]);

  useEffect(() => {
    if (open) return;
    setCropModalOpen(false);
    setUploadingPicture(false);
    if (pictureInputRef.current) pictureInputRef.current.value = "";
    setSelectedCropSource((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [open]);

  const handleProfilePicturePick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo imágenes JPG, PNG, WEBP o GIF.");
      event.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Máximo 5 MB.");
      event.target.value = "";
      return;
    }
    if (!contactId) return;
    setSelectedCropSource((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setCropModalOpen(true);
  };

  const handleCropModalClose360 = () => {
    setCropModalOpen(false);
    setSelectedCropSource((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (pictureInputRef.current) pictureInputRef.current.value = "";
  };

  const handleProfilePictureCropped = async (croppedImageUrl) => {
    setCropModalOpen(false);
    setUploadingPicture(true);
    try {
      if (
        !croppedImageUrl ||
        (!croppedImageUrl.startsWith("blob:") && !croppedImageUrl.startsWith("data:"))
      ) {
        toast.error("Imagen inválida. Intenta de nuevo.");
        handleCropModalClose360();
        setUploadingPicture(false);
        return;
      }

      let response;
      try {
        response = await fetch(croppedImageUrl);
      } catch {
        toast.error("No se pudo leer la imagen recortada (CSP/red).");
        setUploadingPicture(false);
        return;
      }
      if (!response.ok) throw new Error(`Fetch imagen ${response.status}`);

      const blob = await response.blob();
      const file = new File([blob], "profile-picture.jpg", { type: "image/jpeg" });
      const formData = new FormData();
      formData.append("picture", file);

      const { data } = await api.post(`/contacts/${contactId}/picture`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success(data?.message || "Foto actualizada.");
      setAvatarKey((n) => n + 1);
      await loadSummary();
      onContactUpdated?.();
    } catch (err) {
      toastError(err);
    } finally {
      if (mountedRef.current) setUploadingPicture(false);
      setSelectedCropSource((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      if (pictureInputRef.current) pictureInputRef.current.value = "";
    }
  };

  const contact = summary?.contact;
  const kpis = summary?.kpis || {};
  const negocio = summary?.negocio || null;
  const permissions = summary?.permissions || {};
  const aiExtractedData = contact?.aiExtractedData || {};
  const appointments = Array.isArray(summary?.appointments) ? summary.appointments : [];
  const tags = Array.isArray(draft.contactTags) ? draft.contactTags : [];

  const whatsappConnName = contact?.whatsapp?.name || null;

  const isBeautyProfile = useMemo(() => isBeautyBusinessProfile(onboardingConfig), [onboardingConfig]);

  const serviceSelectOptions = useMemo(() => {
    const svc = onboardingConfig?.services;
    if (!Array.isArray(svc)) return [];
    return svc.map((s) => ({
      value: String(s.id),
      label: String(s.serviceName || s.name || `#${s.id}`)
    }));
  }, [onboardingConfig]);

  const professionalSelectOptions = useMemo(() => {
    const pr = onboardingConfig?.professionals;
    if (!Array.isArray(pr)) return [];
    return pr.map((p) => ({
      value: String(p.id),
      label: String(p.name || `#${p.id}`)
    }));
  }, [onboardingConfig]);

  const paymentSelectOptions = useMemo(() => buildPaymentMethodOptions(onboardingConfig || {}), [onboardingConfig]);

  const serviceIdSet = useMemo(() => new Set(serviceSelectOptions.map((o) => o.value)), [serviceSelectOptions]);
  const professionalIdSet = useMemo(
    () => new Set(professionalSelectOptions.map((o) => o.value)),
    [professionalSelectOptions]
  );

  const referralCodeSet = useMemo(() => new Set(REFERRAL_SOURCE_OPTIONS.map((o) => o.value)), []);

  const legacyServicioLabel =
    draft.servicioFavorito &&
    String(draft.servicioFavorito).trim() !== "" &&
    !serviceIdSet.has(String(draft.servicioFavorito))
      ? String(draft.servicioFavorito)
      : null;

  const legacyProfesionalLabel =
    draft.profesional &&
    String(draft.profesional).trim() !== "" &&
    !professionalIdSet.has(String(draft.profesional))
      ? String(draft.profesional)
      : null;

  const legacyReferralLabel =
    draft.comoNosConocio &&
    String(draft.comoNosConocio).trim() !== "" &&
    !referralCodeSet.has(String(draft.comoNosConocio).toLowerCase())
      ? String(draft.comoNosConocio)
      : null;

  const paymentCodeSet = useMemo(() => new Set(paymentSelectOptions.map((o) => String(o.value))), [paymentSelectOptions]);

  const legacyMetodoPagoLabel =
    draft.metodoPago &&
    String(draft.metodoPago).trim() !== "" &&
    !paymentCodeSet.has(String(draft.metodoPago))
      ? String(draft.metodoPago)
      : null;

  const formattedProxima = useMemo(() => {
    const dt = kpis?.proximaCita?.appointmentDate;
    if (!dt) return "Sin registrar";
    return moment(dt).format("DD/MM HH:mm");
  }, [kpis]);

  const freqLabel =
    kpis.frecuenciaDiasPromedio != null && kpis.frecuenciaDiasPromedio > 0
      ? `${kpis.frecuenciaDiasPromedio} d`
      : "Sin registrar";

  const valorLabel =
    typeof kpis.valorTotal === "number"
      ? `${kpis.valorMoneda || "$"} ${kpis.valorTotal.toFixed(2)}`
      : "Sin registrar";

  const handleDraftChange = (key, raw) => {
    setDraft((prev) => ({ ...prev, [key]: raw }));
  };

  const mergeNewTags = (extra) => {
    const base = Array.isArray(draft.contactTags) ? [...draft.contactTags] : [];
    const seen = new Set(base.map((t) => String(t).trim().toLowerCase()).filter(Boolean));
    extra.forEach((t) => {
      const n = String(t).trim().toLowerCase();
      if (!n || seen.has(n)) return;
      seen.add(n);
      base.push(t.trim());
    });
    setDraft((prev) => ({ ...prev, contactTags: base.slice(0, 10) }));
  };

  const addTagsFromInput = () => {
    const parts = tagDraft
      .split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    mergeNewTags(parts);
    setTagDraft("");
  };

  const removeTag = (tag) => {
    setDraft((prev) => ({
      ...prev,
      contactTags: (prev.contactTags || []).filter((t) => t !== tag)
    }));
  };

  const appendNotaInterna = () => {
    const t = notaInput.trim();
    if (t.length < 3) {
      toast.error("La nota debe tener al menos 3 caracteres");
      return;
    }
    setDraft((prev) => ({
      ...prev,
      notasInternasLog: [
        ...(Array.isArray(prev.notasInternasLog) ? prev.notasInternasLog : []),
        { text: t, createdAt: new Date().toISOString() }
      ]
    }));
    setNotaInput("");
  };

  const handleSaveProfile = async () => {
    if (!contactId) return;
    const name = identity.name.trim();
    const numberDigits = identity.number.replace(/\D/g, "");
    const email = identity.email.trim();
    if (name.length < 2 || name.length > 50) {
      toast.error("El nombre debe tener entre 2 y 50 caracteres.");
      return;
    }
    if (numberDigits.length < 8 || numberDigits.length > 50) {
      toast.error("El número de WhatsApp debe tener entre 8 y 50 dígitos.");
      return;
    }
    if (email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("El correo no es válido.");
      return;
    }

    const profilePatch = {};
    PROFILE_PATCH_KEYS.forEach((k) => {
      const v = draft[k];
      profilePatch[k] = v === "" || v == null ? null : v;
    });
    profilePatch.contactTags = Array.isArray(draft.contactTags) ? [...draft.contactTags] : [];
    profilePatch.excludeFromSequences = !!draft.excludeFromSequences;

    profilePatch.notasInternasLog = Array.isArray(draft.notasInternasLog)
      ? draft.notasInternasLog.map((x) =>
          x && typeof x === "object"
            ? {
                text: String(x.text || "").trim(),
                createdAt: typeof x.createdAt === "string" ? x.createdAt : undefined,
                userId: typeof x.userId === "number" ? x.userId : undefined
              }
            : null
        )
      : [];
    profilePatch.notasInternasLog = profilePatch.notasInternasLog.filter((x) => x && x.text);
    profilePatch.notasInternas = null;
    profilePatch.conexionOrigen = null;

    try {
      setSaving(true);
      const identityRes = await api.put(`/contacts/${contactId}`, {
        name,
        number: numberDigits,
        email
      });

      if (identityRes?.data?.warning === "NUMBER_NOT_IN_WHATSAPP") {
        toast.warning(
          identityRes.data.message ||
            i18n.t("backendErrors.NUMBER_NOT_IN_WHATSAPP") ||
            "Contacto guardado; el número no está registrado en WhatsApp.",
          { autoClose: 5000 }
        );
      }

      const savedRow = identityRes?.data;
      if (savedRow && mountedRef.current) {
        setIdentity({
          name: savedRow.name != null ? String(savedRow.name) : name,
          number: savedRow.number != null ? String(savedRow.number) : numberDigits,
          email: savedRow.email != null ? String(savedRow.email) : email
        });
      }

      await api.put(`/contacts/${contactId}/profile-360`, { profilePatch });
      if (!mountedRef.current) return;
      toast.success("Contacto y perfil guardados");
      await loadSummary();
      onContactUpdated?.();
    } catch (e) {
      toastError(e);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const persistExcludeFromSequences = async (next) => {
    if (!contactId) return;
    try {
      setSavingExclude(true);
      await api.put(`/contacts/${contactId}/profile-360`, { profilePatch: { excludeFromSequences: next } });
      if (!mountedRef.current) return;
      toast.success("Preferencia de secuencias actualizada");
      await loadSummary();
      onContactUpdated?.();
    } catch (e) {
      toastError(e);
    } finally {
      if (mountedRef.current) setSavingExclude(false);
    }
  };

  const restricted = !!permissions.restrictedMessage;
  const showAgendaKpis = !negocio || (!restricted && Number(kpis.totalCitasCount || 0) > 0);

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        className: classes.drawerPaper
      }}
      ModalProps={{
        keepMounted: false,
        disableScrollLock: true,
        style: { zIndex: CONTACT360_DRAWER_Z }
      }}
    >
      <Box component="header" role="banner" className={classes.drawerHeader}>
        <Typography component="div" variant="h6" style={{ flex: 1, fontWeight: 700, color: D11.blue }}>
          Datos del contacto
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <div className={classes.drawerScroll} tabIndex={-1}>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && summary && (
          <>
            {permissions.restrictedMessage && (
              <Paper variant="outlined" style={{ padding: 12, marginBottom: 12, borderColor: D11.orange }}>
                <Typography variant="body2">{permissions.restrictedMessage}</Typography>
              </Paper>
            )}

            <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" style={{ gap: 16 }}>
              <Box className={classes.headerAvatarWrap}>
                {!cropModalOpen && (
                  <>
                    <Avatar
                      key={avatarKey}
                      className={classes.avatarHeader}
                      style={{
                        backgroundColor: generateColor(identity.number || identity.name || contact?.number || ""),
                        color: "white",
                        fontWeight: "bold"
                      }}
                      imgProps={{
                        style: { objectFit: "cover", width: "100%", height: "100%", display: "block" },
                        onError: (e) => {
                          if (e.target) e.target.style.display = "none";
                        },
                        onLoad: (e) => {
                          if (e.target) e.target.style.display = "block";
                        }
                      }}
                      src={
                        contact?.profilePicUrl && !String(contact.profilePicUrl).includes("nopicture.png")
                          ? `${getProfilePicUrl(contact.profilePicUrl)}?t=${avatarKey}`
                          : null
                      }
                      onError={(e) => {
                        if (e.target) e.target.style.display = "none";
                      }}
                    >
                      {contact?.profilePicUrl &&
                      String(contact.profilePicUrl) !== "" &&
                      !String(contact.profilePicUrl).includes("nopicture.png")
                        ? null
                        : getInitials(identity.name || "?")}
                    </Avatar>
                    <IconButton
                      size="small"
                      className={classes.headerAvatarCam}
                      onClick={() => pictureInputRef.current?.click()}
                      disabled={uploadingPicture || saving}
                      aria-label="Cambiar foto de perfil"
                    >
                      {uploadingPicture ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <CameraAltIcon style={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                    <input
                      ref={pictureInputRef}
                      type="file"
                      accept="image/*"
                      className={classes.hiddenFileInput}
                      onChange={handleProfilePicturePick}
                    />
                  </>
                )}
              </Box>
              <Box flex={1} minWidth={160}>
                <Typography component="div" variant="h6">
                  {textOrEmpty(identity.name) || "Sin nombre"}
                </Typography>
                <Typography component="div" variant="body2" style={{ color: D11.placeholder }}>
                  {textOrEmpty(identity.number)}
                </Typography>
                <Typography component="div" variant="body2" color="textSecondary">
                  {textOrEmpty(identity.email) || "Sin correo"}
                </Typography>
                <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                  {negocio ? (
                    <Chip
                      size="small"
                      label={negocio.activeServices > 0 ? "Cliente de la tienda · activo" : "Cliente de la tienda"}
                      style={{ backgroundColor: negocio.activeServices > 0 ? D11.green : D11.blue, color: "#fff" }}
                    />
                  ) : null}
                  {kpis.clienteActiva === true ? (
                    <Chip size="small" label="Cliente activa" style={{ backgroundColor: D11.green, color: "#fff" }} />
                  ) : !negocio ? (
                    <Chip size="small" label="Cliente" variant="outlined" />
                  ) : null}
                  {(tags.length ? tags : [])
                    .map((t, idx) =>
                      typeof t === "string" && t.trim() ? (
                        <Chip key={`${t}-${idx}`} size="small" label={t.trim()} />
                      ) : null
                    )
                    .filter(Boolean)}
                </Box>
              </Box>
            </Box>

            {negocio && (
              <Box display="flex" flexWrap="wrap" style={{ gap: 10 }} mb={showAgendaKpis ? 1.25 : 2}>
                <Paper className={classes.kpiPaper} variant="outlined">
                  <Typography variant="caption">Servicios activos</Typography>
                  <Typography className={classes.kpiValue}>{negocio.activeServices}</Typography>
                </Paper>
                <Paper className={classes.kpiPaper} variant="outlined">
                  <Typography variant="caption">Compras</Typography>
                  <Typography className={classes.kpiValue}>{negocio.purchases}</Typography>
                </Paper>
                <Paper className={classes.kpiPaper} variant="outlined">
                  <Typography variant="caption">Total comprado</Typography>
                  <Typography className={classes.kpiGreen}>{formatNegocioMoney(negocio, negocio.totalSpent)}</Typography>
                </Paper>
                {negocio.customerType === "reseller" && !negocio.pointsEnabled ? null : (
                <Paper className={classes.kpiPaper} variant="outlined">
                  <Typography variant="caption">Saldo</Typography>
                  <Typography className={classes.kpiOrange}>{negocio.pointsEnabled ? formatNegocioWallet(negocio) : "—"}</Typography>
                </Paper>
                )}
              </Box>
            )}

            {showAgendaKpis && (
            <Box display="flex" flexWrap="wrap" style={{ gap: 10 }} mb={2}>
              <Paper className={classes.kpiPaper} variant="outlined">
                <Typography variant="caption">Total visitas</Typography>
                <Typography className={classes.kpiValue}>
                  {restricted ? "—" : kpis.totalVisitas != null ? kpis.totalVisitas : "—"}
                </Typography>
              </Paper>
              <Paper className={classes.kpiPaper} variant="outlined">
                <Typography variant="caption">Próx. cita</Typography>
                <Typography className={classes.kpiGreen}>
                  {restricted ? "Sin registrar" : formattedProxima}
                </Typography>
              </Paper>
              <Paper className={classes.kpiPaper} variant="outlined">
                <Typography variant="caption">Frecuencia</Typography>
                <Typography className={classes.kpiOrange}>{restricted ? "—" : freqLabel}</Typography>
              </Paper>
              <Paper className={classes.kpiPaper} variant="outlined">
                <Typography variant="caption">Valor total</Typography>
                <Typography className={classes.kpiGreen}>{restricted ? "—" : valorLabel}</Typography>
              </Paper>
            </Box>
            )}

            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              classes={{ indicator: classes.tabIndicator }}
              textColor="inherit"
              style={{ color: D11.blue, marginBottom: 8 }}
            >
              <Tab label="Perfil" value={0} />
              {negocio ? <Tab label="Tienda" value={4} /> : null}
              <Tab label="Citas" value={1} />
              <Tab label="Historial" value={2} />
              <Tab label="Mensajes" value={3} />
            </Tabs>

            {tabIndex === 0 && (
              <Box mt={2}>
                <Paper className={classes.bannerIa} elevation={0}>
                  <Typography variant="body2">
                    <strong>Editables (tras «Guardar cambios»):</strong> nombre, WhatsApp, correo y todo el perfil
                    ampliado debajo. La <strong>foto</strong> se guarda al terminar el recorte (no hace falta el botón verde).
                  </Typography>
                </Paper>

                <Typography className={classes.sectionTitle}>IDENTIDAD DEL CONTACTO (columnas principales)</Typography>
                <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginBottom: 8 }}>
                  Estos tres campos persisten en la tabla <strong>Contacts</strong>. Usa «Guardar cambios» para enviarlos
                  junto con el JSON del perfil.
                </Typography>
                <Paper variant="outlined" style={{ padding: "12px 14px", marginBottom: 14, borderRadius: 8 }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    margin="dense"
                    label="Nombre"
                    value={identity.name}
                    onChange={(e) => setIdentity((prev) => ({ ...prev, name: e.target.value }))}
                    disabled={saving || uploadingPicture}
                    style={{ marginBottom: 10 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    margin="dense"
                    label="WhatsApp (solo números recomendado)"
                    placeholder="51980795334"
                    value={identity.number}
                    onChange={(e) => setIdentity((prev) => ({ ...prev, number: e.target.value }))}
                    disabled={saving || uploadingPicture}
                    style={{ marginBottom: 10 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    margin="dense"
                    label="Email"
                    type="email"
                    value={identity.email}
                    onChange={(e) => setIdentity((prev) => ({ ...prev, email: e.target.value }))}
                    disabled={saving || uploadingPicture}
                  />
                </Paper>
                <ProfileDateField
                  label="Fecha nacimiento"
                  fieldKey="fechaNacimiento"
                  value={draft.fechaNacimiento}
                  onChange={handleDraftChange}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                />
                <ProfileAiSelect
                  label="¿Cómo nos conoció?"
                  fieldKey="comoNosConocio"
                  value={draft.comoNosConocio}
                  onChange={handleDraftChange}
                  options={REFERRAL_SOURCE_OPTIONS}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                  extraLegacyOption={legacyReferralLabel}
                />

                <Typography className={classes.sectionTitle}>TAGS CABECERA (máx. 10)</Typography>
                <Box display="flex" flexWrap="wrap" mb={1} style={{ gap: 6 }}>
                  {(tags.length ? tags : []).map((t) => (
                    <Chip key={t} label={t} onDelete={() => removeTag(t)} size="small" />
                  ))}
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Ej. tinte frecuente — Enter o botón para añadir"
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTagsFromInput();
                    }
                  }}
                  disabled={saving || tags.length >= 10}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" onClick={addTagsFromInput} disabled={tags.length >= 10 || saving}>
                          Añadir
                        </Button>
                      </InputAdornment>
                    )
                  }}
                />

                <Typography className={classes.sectionTitle}>PREFERENCIAS DEL SERVICIO</Typography>
                {!serviceSelectOptions.length && onboardingConfig && (
                  <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginBottom: 8 }}>
                    No hay servicios en onboarding (Agenda configuración); elige catálogo allí primero.
                  </Typography>
                )}
                <ProfileAiSelect
                  label="Servicio favorito"
                  fieldKey="servicioFavorito"
                  value={draft.servicioFavorito}
                  onChange={handleDraftChange}
                  options={serviceSelectOptions}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                  extraLegacyOption={legacyServicioLabel}
                />
                {!professionalSelectOptions.length && onboardingConfig && (
                  <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginBottom: 8 }}>
                    No hay equipo/profesionales cargados en onboarding.
                  </Typography>
                )}
                <ProfileAiSelect
                  label="Profesional de preferencia"
                  fieldKey="profesional"
                  value={draft.profesional}
                  onChange={handleDraftChange}
                  options={professionalSelectOptions}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                  extraLegacyOption={legacyProfesionalLabel}
                />
                <ProfileAiSelect
                  label="Horario habitual"
                  fieldKey="horarioHabitual"
                  value={draft.horarioHabitual}
                  onChange={handleDraftChange}
                  options={HORARIO_HABITUAL_OPTIONS}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                />
                {isBeautyProfile ? (
                  <>
                    <ProfileField
                      label="Tipo cabello / restricciones"
                      fieldKey="tipoCabello"
                      value={draft.tipoCabello}
                      onChange={handleDraftChange}
                      aiExtractedData={aiExtractedData}
                      disabled={saving}
                    />
                    <ProfileField
                      label="Alergias"
                      fieldKey="alergias"
                      value={draft.alergias}
                      onChange={handleDraftChange}
                      aiExtractedData={aiExtractedData}
                      disabled={saving}
                    />
                  </>
                ) : null}

                <Typography className={classes.sectionTitle}>INFORMACIÓN COMERCIAL</Typography>
                <ProfileAiSelect
                  label="Método de pago habitual"
                  fieldKey="metodoPago"
                  value={draft.metodoPago}
                  onChange={handleDraftChange}
                  options={paymentSelectOptions}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                  extraLegacyOption={legacyMetodoPagoLabel}
                />
                <ProfileAiSelect
                  label="Frecuencia promedio de visita"
                  fieldKey="frecuenciaVisita"
                  value={draft.frecuenciaVisita}
                  onChange={handleDraftChange}
                  options={FRECUENCIA_VISITA_OPTIONS}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                />
                <ProfileAiSelect
                  label="Asesor asignado"
                  fieldKey="asesorAsignado"
                  value={draft.asesorAsignado}
                  onChange={handleDraftChange}
                  options={professionalSelectOptions}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                />
                <Box mb={2} mt={1}>
                  <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                    Conexión de origen
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Conexión en sistema: {whatsappConnName || "—"}
                  </Typography>
                  <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginTop: 4 }}>
                    Solo lectura (WhatsApp del contacto). No se duplica en el JSON del perfil al guardar.
                  </Typography>
                </Box>

                <Typography className={classes.sectionTitle}>OBSERVACIONES DEL EQUIPO</Typography>
                <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginBottom: 8 }}>
                  Añade notas con el botón +; se guardan al pulsar «Guardar cambios».
                </Typography>
                <Box mb={1}>
                  {(Array.isArray(draft.notasInternasLog) ? draft.notasInternasLog : []).map((n, idx) => (
                    <Paper
                      key={`${idx}-${n.createdAt || ""}-${n.text?.slice(0, 12)}`}
                      variant="outlined"
                      style={{ padding: "8px 10px", marginBottom: 8, borderRadius: 8 }}
                    >
                      {n.createdAt ? (
                        <Typography variant="caption" style={{ color: D11.placeholder, display: "block" }}>
                          {moment(n.createdAt).format("DD/MM/YYYY HH:mm")}
                        </Typography>
                      ) : null}
                      <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                        {n.text}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
                <Box display="flex" alignItems="flex-start" style={{ gap: 8 }} mb={2}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    multiline
                    rows={2}
                    placeholder="Nueva nota interna (mín. 3 caracteres)…"
                    value={notaInput}
                    onChange={(e) => setNotaInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        appendNotaInterna();
                      }
                    }}
                    disabled={saving}
                  />
                  <IconButton
                    color="primary"
                    aria-label="Añadir nota interna"
                    onClick={appendNotaInterna}
                    disabled={saving}
                    style={{ marginTop: 4 }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                <Button
                  variant="contained"
                  className={classes.saveBtn}
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={22} color="inherit" /> : "Guardar cambios"}
                </Button>
              </Box>
            )}

            {tabIndex === 1 &&
              (restricted ? (
                <Box py={4} px={2}>
                  <Typography variant="body2">{permissions.restrictedMessage}</Typography>
                </Box>
              ) : (
                <Contact360CitasTab
                  appointments={appointments}
                  kpis={kpis}
                  classes={classes}
                  excludeFromSequences={!!draft.excludeFromSequences}
                  savingExclude={savingExclude}
                  onExcludeChange={persistExcludeFromSequences}
                />
              ))}
            {tabIndex === 2 && (
              <Contact360HistorialTab
                classes={classes}
                loading={historial.loading}
                loadingMore={historial.loadingMore}
                events={historial.events}
                iaHints={historial.iaHints}
                total={historial.total}
                timelineRestrictedMsg={historial.timelineRestrictedMsg}
                onLoadMore={() => fetchTimelinePage(false)}
              />
            )}
            {tabIndex === 3 && contactId ? <Contact360MensajesTab classes={classes} contactId={contactId} /> : null}
            {tabIndex === 4 && negocio ? <Contact360TiendaTab negocio={negocio} classes={classes} /> : null}
          </>
        )}
      </div>
    </Drawer>
      <ImageCropModal
        open={cropModalOpen}
        onClose={handleCropModalClose360}
        imageSrc={selectedCropSource}
        onCropComplete={handleProfilePictureCropped}
        aspect={1}
        shape="round"
        modalZIndex={CONTACT360_DRAWER_Z + 80}
      />
    </>
  );
}
